/**
 * Детальная карточка партнёра — /partners/{tg_id}.
 *
 * Layout:
 *   1. Header: имя, @username, tg_id, кнопки (← Назад, открыть профиль юзера)
 *   2. KPI: 4 карточки — привёл / активировал / оплатил / выручка
 *   3. Конверсии — 2 progress bars (activation rate, paid conversion)
 *   4. Профиль партнёра — created_at, last_seen_at, заметки админа
 *   5. Список рефералов — таблица всех приведённых юзеров с метриками
 */
import { useNavigate, useParams, Link } from "react-router";
import { useShow } from "@refinedev/core";
import {
  Card, Row, Col, Statistic, Typography, Space, Button, Tag, Table,
  Skeleton, Result, Empty, Descriptions, Progress, Tooltip,
} from "antd";
import {
  ArrowLeftOutlined, UserOutlined, UserAddOutlined,
  CheckCircleOutlined, CreditCardOutlined, DollarOutlined,
} from "@ant-design/icons";
import { fmtDateTime } from "../../utils";

interface Referral {
  tg_id: number;
  first_name: string | null;
  last_name: string | null;
  tg_username: string | null;
  referred_at: string | null;
  bot_blocked: boolean;
  account_deleted: boolean;
  last_seen_at: string | null;
  paid_subs: number;
  revenue_rub: number;
}

interface PartnerDetail {
  tg_id: number;
  first_name: string | null;
  last_name: string | null;
  tg_username: string | null;
  created_at: string | null;
  last_seen_at: string | null;
  bot_blocked: boolean;
  account_deleted: boolean;
  admin_notes: string | null;
  stats: {
    invited: number;
    activated: number;
    paid: number;
    revenue_rub: number;
    activation_rate: number;
    paid_conversion_rate: number;
  };
  referrals: Referral[];
}

const fmtRub = (n: number): string => {
  if (n === 0) return "0 ₽";
  return new Intl.NumberFormat("ru-RU").format(Math.round(n)) + " ₽";
};

export const PartnersShow = () => {
  const { id } = useParams<{ id: string }>();
  const tgId = Number(id);
  const nav = useNavigate();

  const { query } = useShow<PartnerDetail>();
  const data = query?.data?.data;

  if (query?.isLoading) {
    return <Card><Skeleton active paragraph={{ rows: 10 }} /></Card>;
  }
  if (query?.isError) {
    const e: any = query?.error;
    if (e?.statusCode === 404) {
      return (
        <Result status="404" title="Партнёр не найден"
          subTitle={`Пользователь с tg_id=${tgId} либо не существует, либо ещё никого не привёл.`}
          extra={<Button type="primary" onClick={() => nav("/partners")}>
            ← К списку партнёров
          </Button>} />
      );
    }
    return (
      <Result status="error" title="Не удалось загрузить партнёра"
        subTitle={String(e?.message || e)}
        extra={<Button onClick={() => query?.refetch()}>Повторить</Button>} />
    );
  }
  if (!data) return <Empty description="Нет данных" />;

  const name = [data.first_name, data.last_name].filter(Boolean).join(" ") || "—";
  const s = data.stats;

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12,
        alignItems: "center", justifyContent: "space-between",
        marginBottom: 16,
      }}>
        <Space size={12} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => nav("/partners")}>
            К партнёрам
          </Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            👤 {name}
          </Typography.Title>
          {data.tg_username && (
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
              @{data.tg_username}
            </Typography.Text>
          )}
          <Typography.Text code style={{ fontSize: 12 }}>{data.tg_id}</Typography.Text>
          {data.bot_blocked && <Tag color="warning">Заблокировал бота</Tag>}
          {data.account_deleted && <Tag color="default">Удалил аккаунт</Tag>}
        </Space>
        <Space size={6} wrap>
          <Link to={`/users/${data.tg_id}`}>
            <Button icon={<UserOutlined />}>
              Профиль юзера
            </Button>
          </Link>
        </Space>
      </div>

      {/* KPI карточки */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Привёл всего" value={s.invited}
              prefix={<UserAddOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderColor: "#52c41a33" }}>
            <Statistic title="Активировали бот" value={s.activated}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderColor: "#1677ff33" }}>
            <Statistic title="Оплатили VPN" value={s.paid}
              prefix={<CreditCardOutlined style={{ color: "#1677ff" }} />}
              valueStyle={{ color: "#1677ff" }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderColor: "#faad1433" }}>
            <Statistic title="Выручка" value={fmtRub(s.revenue_rub)}
              prefix={<DollarOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14", fontSize: 18 }} />
          </Card>
        </Col>
      </Row>

      {/* Конверсии + Профиль */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card title="📈 Конверсия воронки" size="small">
            <div style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Из перешедших по ссылке — зашли в бота
              </Typography.Text>
              <Progress percent={s.activation_rate} size="small"
                status={s.activation_rate >= 80 ? "success" : "active"} />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {s.activated} из {s.invited}
              </Typography.Text>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Из зашедших в бот — оплатили подписку
              </Typography.Text>
              <Progress percent={s.paid_conversion_rate} size="small"
                strokeColor="#faad14" />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {s.paid} из {s.activated}
              </Typography.Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="🪪 Профиль партнёра" size="small">
            <Descriptions column={1} size="small" colon>
              <Descriptions.Item label="Создан">
                {fmtDateTime(data.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Последний раз в боте">
                {fmtDateTime(data.last_seen_at)}
              </Descriptions.Item>
              {data.admin_notes && (
                <Descriptions.Item label="Заметки админа">
                  <Typography.Text style={{ whiteSpace: "pre-wrap" }}>
                    {data.admin_notes}
                  </Typography.Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Список рефералов */}
      <Card title={
        <Space>
          <span>👥 Приведённые юзеры</span>
          <Tag>{data.referrals.length}</Tag>
        </Space>
      } size="small">
        <Table<Referral> dataSource={data.referrals} rowKey="tg_id" size="small"
          pagination={{ pageSize: 25 }}>
          <Table.Column<Referral> dataIndex="tg_id" title="TG ID" width={120}
            render={(v: number) => (
              <Link to={`/users/${v}`}>
                <Typography.Text code style={{ fontSize: 12 }}>{v}</Typography.Text>
              </Link>
            )} />
          <Table.Column<Referral> title="Юзер"
            render={(_, r) => {
              const nm = [r.first_name, r.last_name].filter(Boolean).join(" ") || "—";
              return (
                <Space size={4}>
                  <Link to={`/users/${r.tg_id}`}>{nm}</Link>
                  {r.tg_username && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      @{r.tg_username}
                    </Typography.Text>
                  )}
                </Space>
              );
            }} />
          <Table.Column<Referral> title="Статус" width={150}
            render={(_, r) => {
              if (r.account_deleted) return <Tag color="default">Удалил TG</Tag>;
              if (r.bot_blocked)     return <Tag color="warning">Заблокировал бота</Tag>;
              if (r.paid_subs > 0)   return <Tag color="gold">💰 Платящий</Tag>;
              return <Tag color="green">Активен</Tag>;
            }} />
          <Table.Column<Referral> dataIndex="paid_subs" title="Оплат" width={90}
            render={(v: number) =>
              v > 0
                ? <Tag color="gold">{v}</Tag>
                : <Typography.Text type="secondary">—</Typography.Text>
            } />
          <Table.Column<Referral> dataIndex="revenue_rub" title="Выручка" width={110}
            render={(v: number) =>
              v > 0
                ? <Typography.Text strong>{fmtRub(v)}</Typography.Text>
                : <Typography.Text type="secondary">—</Typography.Text>
            } />
          <Table.Column<Referral> dataIndex="referred_at" title="Привёлся" width={160}
            render={(v: string | null) =>
              v
                ? <Typography.Text style={{ fontSize: 12 }}>{fmtDateTime(v)}</Typography.Text>
                : <Typography.Text type="secondary">—</Typography.Text>
            } />
          <Table.Column<Referral> dataIndex="last_seen_at" title="Был в боте" width={160}
            render={(v: string | null) =>
              v
                ? <Typography.Text style={{ fontSize: 12 }}>{fmtDateTime(v)}</Typography.Text>
                : <Typography.Text type="secondary">не был</Typography.Text>
            } />
        </Table>
      </Card>
    </div>
  );
};
